// Libs
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
// Utils
import { CloudStorage, platform } from 'components/AppConfig.json';
// Services
import { ApiServiceV2 } from 'services';
// Components
import {
	Wrapper,
	Popover,
	Spin,
	EyeIcon,
	SettingsIcon,
	Span,
	RefreshIcon,
	LogoIcon,
	Menu,
} from 'components';
// Hooks
import useToastMessage from 'hooks/useToastMessage';
// Selectors
import { selectProfile } from 'screens/Login/selectors';

const OptionsMenu = ({
	isSpinning = false,
	defaultSelectKey = '',
	groupItems = [],
}) => {
	// TODO: CheckSelectDefault
	return isSpinning ? (
		<Spin spinning size='small' delay={50} tip={''} />
	) : (
		<Menu.Wrapper>
			{groupItems.map(group =>
				group.isSpinning && group.isSpinning ? (
					<Spin spinning size='small' delay={50} tip={''} />
				) : (
					<Menu.Group
						defaultSelectedKeys={defaultSelectKey}
						title={group.title}
					>
						{group.items.map(item =>
							item.isSpinning ? (
								<Spin spinning size='small' delay={50} tip={''} />
							) : (
								<Menu.Item
									disabled={item.disabled}
									onClick={() => item.onClick()}
									key={item.key}
								>
									{item.title}
								</Menu.Item>
							),
						)}
					</Menu.Group>
				),
			)}
		</Menu.Wrapper>
	);
};

const getReportUrl = ({ paymentId, profile }) => {
	const env = process.env.NODE_ENV;
	const organization = profile.organization.settings.photoStore.main;
	const company = profile.company.settings.photoStore.main;
	const entity_id = profile.entity.id;
	const reportUri = `${CloudStorage}/${platform}/${env}/${company}/${organization}/reports/payments/${entity_id}`;

	let reportId = `${paymentId}.pdf`;

	return `${reportUri}/${reportId}`;
};

const GetOptionsResumeContent = ({
	paymentId,
	profile,
	state,
	setState,
	paymentState,
}) => {
	const sendToast = useToastMessage();
	const reportUrl = getReportUrl({
		paymentId,
		profile,
	});

	const handdleThen = (message, stateNameRefresh) => {
		sendToast({ message: message, type: 'success' });
		setState({ [stateNameRefresh]: false });
	};

	const handdleCatchError = (err, stateNameRefresh) => {
		sendToast({ message: err.message, type: err.type || 'error' });
		setState({ [stateNameRefresh]: false });
	};

	const setPaymentReopen = () => {
		setState({ isRefreshOpenOption: true });

		ApiServiceV2.put({
			url: `/payments/open`,
			data: { payment_id: paymentId },
		})
			.then(() => handdleThen('Pago Abierto', 'isRefreshOpenOption'))
			.catch(err => handdleCatchError(err, 'isRefreshOpenOption'));
	};

	const setPaymentReady = () => {
		setState({ isRefreshCloseOption: true });

		ApiServiceV2.put({
			url: `/payments/ready`,
			data: { payment_id: paymentId },
		})
			.then(() => handdleThen('Pago Cerrado', 'isRefreshCloseOption'))
			.catch(err => handdleCatchError(err, 'isRefreshCloseOption'));
	};

	const handdleGeneratePaymentPDF = () => {
		setState({ isRefreshPdf: true });

		ApiServiceV2.post({
			url: `/payments/generatePDF`,
			data: { paymentIds: [paymentId] },
		})

			.then(() => {
				handdleThen('PDF Generado', 'isRefreshPdf');
				setState({ isPdfGenered: true });
			})
			.catch(err => handdleCatchError(err, 'isRefreshPdf'));
	};

	const recordsHaveAllowedStates = (state, allowStates) =>
		!!allowStates.find(allowState => allowState === state);

	return (
		<OptionsMenu
			groupItems={[
				{
					title: 'Cambiar Estado',
					items: [
						{
							title: 'Abrir',
							isSpinning: state.isRefreshOpenOption,
							disabled: !recordsHaveAllowedStates(paymentState, [
								'ready',
								'agreed',
								'released',
							]),
							onClick: () => setPaymentReopen(),
							key: 'open',
						},
						{
							title: 'Cerrar',
							isSpinning: state.isRefreshCloseOption,
							disabled: !recordsHaveAllowedStates(paymentState, ['open']),
							onClick: () => setPaymentReady(),
							key: 'Close',
						},
					],
				},
				{
					title: 'Exportar',
					isSpinning: state.isRefreshPdf,
					items: [
						{
							title: `PDF Generar`,
							disabled: !recordsHaveAllowedStates(paymentState, [
								'released',
								'paid',
							]),
							onClick: handdleGeneratePaymentPDF,
							key: 'generatePDF',
						},
						{
							title: `PDF Abrir`,
							disabled:
								!recordsHaveAllowedStates(paymentState, ['released', 'paid']) ||
								!state.isPdfGenered,
							onClick: () => window.open(reportUrl),
							key: 'openPDF',
						},
					],
				},
			]}
		/>
	);
};

const GetModeViewResumeContent = ({ generatingPDF, setMainState }) => (
	<OptionsMenu
		isSpinning={generatingPDF}
		groupItems={[
			{
				title: 'Producción',
				defaultSelectKey: 'individual',
				items: [
					{
						title: 'Individual',
						onClick: () => setMainState({ modeviewGroupOrders: false }),
						key: 'individual',
					},
					{
						title: 'Agrupadas',
						onClick: () => setMainState({ modeviewGroupOrders: true }),
						key: 'group',
					},
				],
			},
		]}
	/>
);

const HeaderSettings = ({
	paymentId,
	isPdfGenered,
	paymentState,
	setMainState,
	getResumePayments,
	isRefreshResumen,
}) => {
	const [state, _setState] = useState({
		isPdfGenered,
		isRefreshPdf: false,

		isRefreshOpenOption: false,
		isRefreshCloseOption: false,
	});
	const profile = useSelector(selectProfile);

	const setState = newState =>
		_setState(current => ({ ...current, ...newState }));

	return (
		<Wrapper width='95%' height='28px' justifyContent='space-between'>
			<Span fontSize='xl'>Resumen</Span>
			<Wrapper padding='0'>
				<Popover
					trigger='click'
					placement='bottomRight'
					title={null}
					content={
						<GetModeViewResumeContent
							generatingPDF={state.isRefreshPdf}
							setMainState={setMainState}
						/>
					}
				>
					<div>
						<EyeIcon button />
					</div>
				</Popover>

				<Popover
					trigger='click'
					placement='bottomRight'
					title={null}
					content={
						<GetOptionsResumeContent
							paymentId={paymentId}
							profile={profile}
							state={state}
							setState={setState}
							paymentState={paymentState}
						/>
					}
				>
					<div>
						<SettingsIcon />
					</div>
				</Popover>

				{isRefreshResumen ? (
					<LogoIcon button spin />
				) : (
					<RefreshIcon onClick={getResumePayments} />
				)}
			</Wrapper>
		</Wrapper>
	);
};

export default HeaderSettings;
